

























import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { compareAsc, format, parse } from 'date-fns';
import TextInput from '@components/020-TextInput/TextInput.vue';
import SelectInput from '@components/021-SelectInput/SelectInput.vue';
import Checkbox from '@components/023-Checkbox/Checkbox.vue';
import FileInput from '@components/024-FileInput/FileInput.vue';
import HiddenInput from '@components/025-HiddenInput/HiddenInput.vue';
import RatingInput from '@components/026-RatingInput/RatingInput.vue';
import DateInput from '@components/053-DateInput/DateInput.vue';
import RadioGroup from '@components/022-RadioGroup/RadioGroup.vue';
import ISfJobApplicationInput from '@frontend/types/modules/form/ISfJobApplicationInput';
import TooltipHint from '@components/015-TooltipHint/TooltipHint.vue';
import { areVisibilityContraintsFulfilled } from '@frontend/utils/SfJobApplication';
import staticContent from '@components/027-StaticContent/StaticContent.vue';
import SfJobSelection from './multi-fields/SfJobSelection.vue';

@Component({
	components: {
		TooltipHint,
		RadioGroup,
		Checkbox,
		SelectInput,
		TextInput,
		FileInput,
		HiddenInput,
		RatingInput,
		staticContent,
		DateInput,
		SfJobSelection
	}
})
export default class FormInput extends Vue {
	@Prop(Object)
	protected readonly field!: ISfJobApplicationInput;

	@Prop({ type: Object, default: () => ({}) })
	protected readonly inputsById!: Record<string, ISfJobApplicationInput>;

	@Prop(Object)
	protected readonly dateFormats!: Record<string, string>;

	protected get error(): string | null {
		if (this.transformedField.error && typeof this.transformedField.error === 'object') {
			// Don't handle complex error objects (they have to be handled by the multi-fields)
			return null;
		}

		return this.transformedField.error;
	}

	protected get type(): string {
		if (['StaticInput', 'Honeypot'].includes(this.field.type)) {
			// 'DateInput'
			return 'TextInput';
		}

		return this.field.type;
	}

	protected get autoComplete(): string | null {
		return this.field.type === 'Honeypot' ? 'off' : null;
	}

	protected get classList(): string[] {
		const classList: string[] = [];

		if (this.field.type === 'SelectInput') classList.push('c-select-input--block');

		return classList;
	}

	/**
	 * Validates if all constraints to make this input visible are fulfilled
	 * @protected
	 */
	protected get visibilityContraintsFulfilled(): boolean {
		return areVisibilityContraintsFulfilled(this.field, Object.values(this.inputsById));
	}

	protected get transformedField(): any {
		const transformedField: any = {
			error: null,
			...this.field
		};

		if (transformedField.required === false) {
			delete transformedField.required;
		}

		if (this.field.type === 'StaticInput') {
			transformedField.type = 'text';
			transformedField.readonly = 'readonly';
			transformedField.disabled = 'disabled';
		} else if (this.field.type === 'HiddenInput') {
			transformedField.type = 'hidden';
		} else if (this.field.type === 'Checkbox') {
			transformedField.value = '1';
		} else if (this.field.type === 'SelectInput') {
			// transformedField.prompt = transformedField.label;
		} else {
			transformedField.type = 'text';

			if (transformedField.validationRule) {
				// we can't use the validation rule as input pattern attribute as this is always case-sensitive,
				// but we can use input types that assist in correct field population
				if (transformedField.validationRule === 'email') {
					transformedField.type = 'email';
				} else if (transformedField.validationRule === 'number') {
					transformedField.type = 'number';
				} else if (transformedField.validationRule === 'url') {
					transformedField.type = 'url';
				}
			}
		}

		return transformedField;
	}

	protected get value(): any {
		return this.field.value;
	}

	protected set value(value: any) {
		Vue.set(this.field, 'value', value);
		this.validateInput();
	}

	protected onInvalid(inputEventOrError: Event | string | Record<string, string | null>): void {
		// only emit error events if visibility constraints are met
		if (!this.visibilityContraintsFulfilled) {
			return;
		}

		// In case a multi field with it's own error handling is nested, no event will be bubbled
		if (inputEventOrError instanceof Event) {
			const input: HTMLInputElement = inputEventOrError.currentTarget as HTMLInputElement;
			let validationerrormessage = input.getAttribute('validationerrormessage');
			if (!validationerrormessage) {
				if (input.parentNode && input.parentNode instanceof HTMLElement) {
					validationerrormessage = input.parentNode.getAttribute('validationerrormessage');
				}
			}
			const errorMessage =
				validationerrormessage !== null && validationerrormessage.trim() !== ''
					? validationerrormessage
					: input.validationMessage;
			Vue.set(this.field, 'error', errorMessage);
		} else {
			Vue.set(this.field, 'error', inputEventOrError);
		}

		this.$emit('invalid', this);
	}

	protected resetError(): void {
		Vue.set(this.field, 'error', null);
	}

	protected validateDateInput(): void {
		const textInputComponent: TextInput = this.$refs.input as TextInput;

		if (!this.dateFormat) throw new Error(`Invalid date format '${this.field.dateFormat}'`);

		textInputComponent.resetError();

		const date: Date = parse(this.field.value, this.dateFormat, new Date());

		const minDate: Date | undefined = this.field.minDate
			? parse(this.field.minDate, this.dateFormat, new Date())
			: undefined;

		const maxDate: Date | undefined = this.field.maxDate
			? parse(this.field.maxDate, this.dateFormat, new Date())
			: undefined;

		// eslint-disable-next-line no-restricted-globals
		if (isNaN(date.getTime()) || format(date, this.dateFormat) !== this.field.value) {
			textInputComponent.setError(this.field.validationErrorMessage);
		} else if (minDate && compareAsc(date, minDate) < 0) {
			textInputComponent.setError(this.field.validationErrorMessage);
		} else if (maxDate && compareAsc(date, maxDate) > 0) {
			textInputComponent.setError(this.field.validationErrorMessage);
		}
	}

	protected validateInput(): void {
		this.resetError();

		switch (this.field.type) {
			case 'DateInput':
				this.validateDateInput();
				break;
			default:
				break;
		}
	}

	protected get dateFormat(): string | undefined {
		if (!this.field.dateFormat) return;
		return this.dateFormats[this.field.dateFormat] || undefined;
	}
}
